/**
 * Copyright 2024 Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import {IAnalyticsService} from "sirius-platform-support-library/shared/analytics/analytics-service.interface";
import {environment} from "../../../environments/environments";
import {ILocalizationService} from "sirius-platform-support-library/shared/localization/localization-service.interface";
import {PendoUserDataService} from "./pendo-user-data.service";
import {Company, ServiceCode} from "./pendo-user-data.model";
import {
  IAuthenticationService
} from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import {
  IEnvironmentCodeProvider
} from "sirius-platform-support-library/shared/environment/environment-code-provider.interface";
import {EnvironmentType} from "sirius-platform-support-library/tenants/tenant-context";

export const TdpAnalyticsServiceTypeName = "TdpAnalyticsService";

export class TdpAnalyticsService {
  public window: Window;
  public analyticsService: IAnalyticsService;
  public localizationService: ILocalizationService;
  public pendoUserDataService: PendoUserDataService;
  public authenticationService: IAuthenticationService;
  public environmentCodeProvider: IEnvironmentCodeProvider;

  private static readonly DELL_EMPLOYEE_REGEX =
    /@dell\.com|@dellteam\.com|@emc\.com|@emconlinesupport\.com|@uatmail\.com$/i;
  private static readonly GLASSBOX_CODE = "GLASSBOX";
  private static readonly PENDO_CODE = "PENDO";
  private static readonly TDP_ENVIRONMENT = ["STAGING", "PROD"]

  public constructor(
    window: Window,
    analyticsService: IAnalyticsService,
    localizationService: ILocalizationService,
    pendUserDataService: PendoUserDataService,
    authenticationService: IAuthenticationService,
    environmentCodeProvider: IEnvironmentCodeProvider
  ) {
    this.window = window;
    this.analyticsService = analyticsService;
    this.localizationService = localizationService;
    this.pendoUserDataService = pendUserDataService;
    this.authenticationService = authenticationService;
    this.environmentCodeProvider = environmentCodeProvider;
  }

  public async init() {
    if (TdpAnalyticsService.TDP_ENVIRONMENT.includes(this.environmentCodeProvider.getCode(EnvironmentType.TENANT))) {
      // Initialize Pendo and Glassbox analytics engines in parallel
      await this.initializePendo();
      await this.initializeGlassbox();
    }
  }

  private async initializeGlassbox(): Promise<void> {
    // Get the current locale and split it into language and country codes
    const localeParts = this.localizationService
      .getCurrentLocale()
      .localeCode.split("_");

    // Default country and language codes
    let countryCode = "us";
    let languageCode = "en";
    // If localeParts has both language and country codes, update the default values
    if (localeParts.length === 2) {
      countryCode = localeParts[1].toLocaleLowerCase();
      languageCode = localeParts[0].toLocaleLowerCase();
    }

    // Initialize the Glassbox analytics engine with the necessary parameters
    try {
      await this.analyticsService.initializeEngine(
        TdpAnalyticsService.GLASSBOX_CODE,
        {
          scriptUrl: environment.glassboxScriptUrl,
          applicationName: "ThirdParty:TechDirect",
          metrics: {
            country: countryCode,
            language: languageCode,
            segment: "corp",
            customerset: "",
            cms: "techdirect",
            supportappindex: "890.000.061",
            pagename: `${countryCode}|${languageCode}|corp|techdirect|TDM|home`,
            applicationName: "ThirdParty:TechDirect",
          },
        }
      );
      // Enable the Glassbox analytics engine
      await this.analyticsService.enableEngine(
        TdpAnalyticsService.GLASSBOX_CODE
      );
    } catch (error) {
      console.warn("Error initializing Glassbox analytics engine", error);
    }
  }

  private async initializePendo(): Promise<void> {
    // If there's no Pendo API key in the environment, return immediately
    if (!environment.pendoApiKey) return;

    // Get the current locale and split it into language and country codes
    const locale = this.localizationService.getCurrentLocale().localeCode;

    // Initialize the Pendo analytics engine with the necessary parameters
    try {
      if (this.authenticationService.isUserAuthenticated()) {
        // Query user data for initializing Pendo
        const pendoData = await this.pendoUserDataService.queryUserData();
        // Get the activations from the current company data
        const activations = this.getActivations(pendoData?.currentCompany);
        // Get the role names from the current user data
        const rolesNames = (pendoData?.currentUser?.roles || [])?.map(
          (role) => role?.name
        );

        await this.analyticsService.initializeEngine(
          TdpAnalyticsService.PENDO_CODE,
          {
            clientKey: environment.pendoApiKey,
            visitor: {
              id: btoa(
                this.getLowerCaseEmailIfExists(pendoData?.currentUser?.email)
              ),
              isEmployee: this.isDellEmployee(
                this.getLowerCaseEmailIfExists(pendoData?.currentUser?.email)
              ),
              activations: activations,
              roles: rolesNames,
              selectedLanguage: locale,
            },
            account: {
              id: pendoData?.currentCompany?.id || null,
              name: pendoData?.currentCompany?.name || null,
            },
          }
        );
      } else {
        await this.analyticsService.initializeEngine(
          TdpAnalyticsService.PENDO_CODE,
          {
            clientKey: environment.pendoApiKey,
            visitor: {
              id: "bnVsbA==",
              selectedLanguage: locale,
            },
            account: {},
          }
        );
      }
      // Enable the Pendo analytics engine
      await this.analyticsService.enableEngine(TdpAnalyticsService.PENDO_CODE);
    } catch (error) {
      console.warn("Error initializing Pendo analytics engine", error);
    }

    // listen for language changes
    this.localizationService.onLocaleChanged(
      this,
      "TdpAnalyticsService",
      this.onLocaleChanged.bind(this)
    );
  }

  onLocaleChanged() {
    this.analyticsService.setOptions(TdpAnalyticsService.PENDO_CODE, {
      clientKey: environment.pendoApiKey,
      visitor: {
        selectedLanguage:
          this.localizationService.getCurrentLocale().localeCode,
      },
    });
  }

  private getActivations(company: Company): string[] {
    // If the company has services, return the service codes excluding 'TOU'
    if (company && company.services) {
      return company.services
        .filter((service) => service.service.serviceCode != ServiceCode.TOU)
        .map((service) => service.service.serviceCode);
    }
    // If the company has no services, return an empty array
    return [];
  }

  private isDellEmployee(email: string): boolean {
    // If the email is null, return null
    if (email == null) {
      return null;
    }
    // Check if the email matches the Dell employee email pattern
    return email.match(TdpAnalyticsService.DELL_EMPLOYEE_REGEX) != null;
  }

  private getLowerCaseEmailIfExists(currentUserEmail: string) {
    // If the email is null, return null
    if (currentUserEmail == null) {
      return null;
    }
    // Convert the email to lower case
    return currentUserEmail.toLowerCase();
  }
}
